






































































import Enumerable from "linq";
import { Component, Mixins, Prop, Emit } from "vue-property-decorator";
import { Directory } from "../../../../../../base/domain/drawing";
import { DomainRegistry } from "../../../../../domain";
import { EditableFormatSummary } from "../../../../../domain/editableFormat";
import EditableFormatSummaryTreeNameEditDialog from "./EditableFormatSummaryTreeNameEditDialog.vue";
import EditableFormatSummaryTreeEditor from "./EditableFormatSummaryTreeEditor.vue";
import { DialogMixin, PdfStyleDrawableMixin } from "../../../../../../base/components/system/mixins";
import { Separator } from "../../../../../../base/components/system";
import { PdfViewer } from "../../../../../../base/components/system/viewers";

@Component({
  components: {
    NameEditDialog: EditableFormatSummaryTreeNameEditDialog,
    TreeEditor: EditableFormatSummaryTreeEditor,
    Separator,
    PdfViewer,
  }
})
export default class EditableFormatSummaryTreeEditDialog extends Mixins(DialogMixin, PdfStyleDrawableMixin) {
  editingDirectories: Directory<number, EditableFormatSummary>[] = [];
  selectedDirectory: Directory<number, EditableFormatSummary> = null;
  selectedFormat: EditableFormatSummary = null;
  selectorWidth: number = 750;
  isRegistering: boolean = false;

  $refs!: {
    nameEditDialog: EditableFormatSummaryTreeNameEditDialog,
    treeEditor: EditableFormatSummaryTreeEditor,
  };

  @Prop()
  directories: Directory<number, EditableFormatSummary>[];

  @Emit("needReloaded")
  needReloaded(): void {}

  get isSelectedDirectory(): boolean {
    return this.selectedDirectory instanceof Directory;
  }

  get isSelectedEditableFormatSummary(): boolean {
    return this.selectedFormat instanceof EditableFormatSummary;
  }

  get selectedPreviewSource(): string {
    return (this.isSelectedEditableFormatSummary && this.selectedFormat.previewSource)
      ? this.selectedFormat.previewSource.source
      : "";
  }

  get formatNameWording(): string {
    return this.$wording.format.name;
  }

  async show(): Promise<void> {
    return this.showModal(async () => {
      this.reload();
      await this.waitHidden();
      this.editingDirectories = null; // 次回表示を滑らかにするために削除しておく
    });
  }

  reload(): void {
    this.clear();
    this.editingDirectories = Enumerable.from(this.directories)
      .select(r => r.clone())
      .toArray();
  }

  private clear(): void {
    this.editingDirectories = null;
    this.selectedDirectory = null;
    this.selectedFormat = null;
  }

  async showAddFolder(): Promise<void> {
    const dialog = this.$refs.nameEditDialog;
    await dialog.show(true, true, "");
    if (!dialog.result) {
      return;
    }
    try {
      this.isRegistering = true;
      if (!await this.checkAddFolder()) {
        this.needReloaded();
        return;
      }
      const repository = DomainRegistry.instance.editableFormat.getFormatRepository();
      const id = await repository.addDirectory(
        dialog.modifiedText,
        this.selectedDirectory !== null ? this.selectedDirectory.key : null,
        this.selectedDirectory !== null ? this.getNextSort(this.selectedDirectory.nodes) : this.getNextSort(this.editingDirectories));
      this.$refs.treeEditor.addFolder(id, dialog.modifiedText);
      await this.updateEditingDirectories();
      this.$success(
        "フォルダ追加完了",
        "フォルダを追加しました。",
      );
    } catch (e) {
      this.$error(
        "フォルダ追加失敗",
        "フォルダの追加に失敗しました。状態を維持できないため、ツリーを再読み込みします。",
        e);
      this.needReloaded();
    } finally {
      this.isRegistering = false;
    }
  }

  private async checkAddFolder(): Promise<boolean> {
    const editingDirectories = await this.getFormatTree();
    const directory = this.toHashTableDirectory(editingDirectories);
    if (this.selectedDirectory !== null) {
      if (!(directory[this.selectedDirectory.key] instanceof Directory)) {
        this.$warn(
          "フォルダ追加中止",
          "フォルダが既に削除されています。状態を維持できないため、ツリーを再読み込みします。");
        return false;
      }
    }
    return true;
  }

  private getNextSort(nodes: Directory<number, EditableFormatSummary>[]): number {
    return nodes.length > 0
      ? Enumerable.from(nodes).max(n => typeof n.sort === "number" ? n.sort : 0) + 1
      : 1;
  }

  async showEditFolder(): Promise<void> {
    if (!this.isSelectedDirectory) {
      return;
    }
    const dialog = this.$refs.nameEditDialog;
    await dialog.show(true, false, this.selectedDirectory.name);
    if (!dialog.result) {
      return;
    }
    try {
      this.isRegistering = true;
      if (!await this.checkRenameDirectory()) {
        this.needReloaded();
        return;
      }
      const repository = DomainRegistry.instance.editableFormat.getFormatRepository();
      await repository.renameDirectory(this.selectedDirectory.key, dialog.modifiedText);
      this.$refs.treeEditor.setNodeLabel(dialog.modifiedText);
      await this.updateEditingDirectories();
      this.$success(
        "フォルダ名変更完了",
        "フォルダ名を変更しました。",
      );
    } catch (e) {
      this.$error(
        "フォルダ名変更失敗",
        "フォルダ名の変更に失敗しました。状態を維持できないため、ツリーを再読み込みします。",
        e);
      this.needReloaded();
    } finally {
      this.isRegistering = false;
    }
  }

  private async checkRenameDirectory(): Promise<boolean> {
    const editingDirectories = await this.getFormatTree();
    const directory = this.toHashTableDirectory(editingDirectories);
    if (!(directory[this.selectedDirectory.key] instanceof Directory)) {
      this.$warn(
        "フォルダ名変更中止",
        "フォルダが既に削除されています。状態を維持できないため、ツリーを再読み込みします。");
      return false;
    }
    return true;
  }

  async deleteFolder(): Promise<void> {
    if (!this.isSelectedDirectory) {
      return;
    }
    try {
      this.beginModal();
      const index = await this.$commonDialogs.confirmation.confirmYesNo(
        "フォルダ削除確認",
        ["選択中のフォルダを削除します。本当によろしいですか？"]
      );
      if (index !== 0) { return; }
      const result = this.containsFormat(this.selectedDirectory);
      if (result) {
        const resultIndex = await this.$commonDialogs.confirmation.confirmYesNo(
          this.formatNameWording + "削除確認",
          ["選択中のフォルダ配下の" + this.formatNameWording + "も削除します。本当によろしいですか？"]
        );
        if (resultIndex !== 0) { return; }
      }
    } catch {
      // キャンセル
      return;
    } finally {
      this.endModal();
    }
    try {
      this.isRegistering = true;
      if (!await this.checkDeleteDirectory()) {
        this.needReloaded();
        return;
      }
      const repository = DomainRegistry.instance.editableFormat.getFormatRepository();
      await repository.deleteDirectory(this.selectedDirectory.key);
      this.$refs.treeEditor.deleteNode();
      this.selectedDirectory = null;
      await this.updateEditingDirectories();
      this.$success(
        "フォルダ削除完了",
        "フォルダを削除しました。",
      );
    } catch (e) {
      this.$error(
        "フォルダ削除失敗",
        "フォルダの削除に失敗しました。状態を維持できないため、ツリーを再読み込みします。",
        e);
      this.needReloaded();
    } finally {
      this.isRegistering = false;
    }
  }

  private containsFormat(dir: Directory<number, EditableFormatSummary>): boolean {
    return (dir.entities.length > 0) || Enumerable.from(dir.nodes).any(n => this.containsFormat(n));
  }

  private async checkDeleteDirectory(): Promise<boolean> {
    const editingDirectories = await this.getFormatTree();
    const directory = this.toHashTableDirectory(editingDirectories);
    if (!(directory[this.selectedDirectory.key] instanceof Directory &&
      Enumerable.from(this.selectedDirectory.entities).sequenceEqual(directory[this.selectedDirectory.key].entities, s => s.id))) {
      this.$warn(
        "フォルダ削除中止",
        "フォルダ内の" + this.formatNameWording + "が別途追加・削除されています。状態を維持できないため、ツリーを再読み込みします。");
      return false;
    }
    return true;
  }

  async showEditFormatTree(): Promise<void> {
    if (!this.isSelectedEditableFormatSummary) {
      return;
    }
    const dialog = this.$refs.nameEditDialog;
    await dialog.show(false, false, this.selectedFormat.name);
    if (!dialog.result) {
      return;
    }
    try {
      this.isRegistering = true;
      if (!await this.checkRenameFormat()) {
        this.needReloaded();
        return;
      }
      const repository = DomainRegistry.instance.editableFormat.getFormatRepository();
      await repository.renameFormat(this.selectedFormat.id, dialog.modifiedText);
      this.$refs.treeEditor.setNodeLabel(dialog.modifiedText);
      await this.updateEditingDirectories();
      this.$success(
        this.formatNameWording + "名変更完了",
        this.formatNameWording + "名を変更しました。",
      );
    } catch (e) {
      this.$error(
        this.formatNameWording + "名変更失敗",
        this.formatNameWording + "名の変更に失敗しました。状態を維持できないため、ツリーを再読み込みします。",
        e);
      this.needReloaded();
    } finally {
      this.isRegistering = false;
    }
  }

  private async checkRenameFormat(): Promise<boolean> {
    const editingDirectories = await this.getFormatTree();
    const summary = this.toHashTableSummary(editingDirectories);
    if (!(summary[this.selectedFormat.id] instanceof EditableFormatSummary)) {
      this.$warn(
        this.formatNameWording + "名変更中止",
        this.formatNameWording + "が既に削除されています。状態を維持できないため、ツリーを再読み込みします。");
      return false;
    }
    return true;
  }

  async deleteFormatTree(): Promise<void> {
    if (!this.isSelectedEditableFormatSummary) {
      return;
    }
    try {
      this.beginModal();
      const index = await this.$commonDialogs.confirmation.confirmYesNo(
        this.formatNameWording + "削除確認",
        ["選択中の" + this.formatNameWording + "を削除します。本当によろしいですか？"]
      );
      if (index !== 0) { return; }
    } catch {
      // キャンセル
      return;
    } finally {
      this.endModal();
    }
    try {
      this.isRegistering = true;
      if (!await this.checkDeleteFormat()) {
        this.needReloaded();
        return;
      }
      const repository = DomainRegistry.instance.editableFormat.getFormatRepository();
      await repository.deleteFormat(this.selectedFormat.id);
      this.$refs.treeEditor.deleteNode();
      this.selectedFormat = null;
      await this.updateEditingDirectories();
      this.$success(
        this.formatNameWording + "削除完了",
        this.formatNameWording + "を削除しました。",
      );
    } catch (e) {
      this.$error(
        this.formatNameWording + "削除失敗",
        this.formatNameWording + "の削除に失敗しました。状態を維持できないため、ツリーを再読み込みします。",
        e);
      this.needReloaded();
    } finally {
      this.isRegistering = false;
    }
  }

  private async checkDeleteFormat(): Promise<boolean> {
    const editingDirectories = await this.getFormatTree();
    const summary = this.toHashTableSummary(editingDirectories);
    if (!(summary[this.selectedFormat.id] instanceof EditableFormatSummary)) {
      this.$warn(
        this.formatNameWording + "削除中止",
        this.formatNameWording + "が既に削除されています。状態を維持できないため、ツリーを再読み込みします。");
      return false;
    }
    return true;
  }

  selectedDirectoryUpdated(value: Directory<number, EditableFormatSummary>): void {
    this.selectedDirectory = value;
    this.selectedFormat = null;
  }

  selectedFormatUpdated(value: EditableFormatSummary): void {
    this.selectedDirectory = null;
    this.selectedFormat = value;
  }

  async movedDirectory(value: Directory<number, EditableFormatSummary>, parent: Directory<number, EditableFormatSummary>, index: number): Promise<void> {
    const repository = DomainRegistry.instance.editableFormat.getFormatRepository();
    try {
      this.isRegistering = true;
      if (!await this.checkMoveDirectory(value)) {
        this.needReloaded();
        return;
      }
      await repository.moveDirectory(value.key, parent !== null ? parent.key : null, index);
      await this.updateEditingDirectories();
      this.$success(
        "フォルダ移動完了",
        "フォルダを移動しました。",
      );
    } catch (e) {
      this.$error(
        "フォルダ移動失敗",
        "フォルダの移動に失敗しました。状態を維持できないため、ツリーを再読み込みします。",
        e);
      this.needReloaded();
    } finally {
      this.isRegistering = false;
    }
  }

  private async checkMoveDirectory(value: Directory<number, EditableFormatSummary>): Promise<boolean> {
    const editingDirectories = await this.getFormatTree();
    const directory = this.toHashTableDirectory(editingDirectories);
    // selectedDirectoryUpdated()でthis.selectedDirectoryが更新されていない場合(クリックしていない場合)があるので引数を評価に使用する
    if (!(directory[value.key] instanceof Directory)) {
      this.$warn(
        "フォルダ移動中止",
        "フォルダが既に削除されています。状態を維持できないため、ツリーを再読み込みします。");
      return false;
    }
    return true;
  }

  async movedFormat(value: EditableFormatSummary, parent: Directory<number, EditableFormatSummary>, index: number): Promise<void> {
    const repository = DomainRegistry.instance.editableFormat.getFormatRepository();
    try {
      this.isRegistering = true;
      if (!await this.checkMoveFormat(value, parent)) {
        this.needReloaded();
        return;
      }
      await repository.moveFormat(value.id, parent !== null ? parent.key : null, index);
      await this.updateEditingDirectories();
      this.$success(
        this.formatNameWording + "移動完了",
        this.formatNameWording + "を移動しました。",
      );
    } catch (e) {
      this.$error(
        this.formatNameWording + "移動失敗",
        this.formatNameWording + "の移動に失敗しました。状態を維持できないため、ツリーを再読み込みします。",
        e);
      this.needReloaded();
    } finally {
      this.isRegistering = false;
    }
  }

  private async checkMoveFormat(value: EditableFormatSummary, parent: Directory<number, EditableFormatSummary>): Promise<boolean> {
    const editingDirectories = await this.getFormatTree();
    const directory = this.toHashTableDirectory(editingDirectories);
    const summary = this.toHashTableSummary(editingDirectories);
    // selectedDirectoryUpdated()でthis.selectedDirectoryが更新されていない場合(クリックしていない場合)があるので引数を評価に使用する
    if (!(directory[parent.key] instanceof Directory)) {
      this.$warn(
        this.formatNameWording + "移動中止",
        "フォルダが既に削除されています。状態を維持できないため、ツリーを再読み込みします。");
      return false;
    }
    // selectedFormatUpdated()でthis.selectedFormatが更新されていない場合(クリックしていない場合)があるので引数を評価に使用する
    if (!(summary[value.id] instanceof EditableFormatSummary)) {
      this.$warn(
        this.formatNameWording + "移動中止",
        this.formatNameWording + "が既に削除されています。状態を維持できないため、ツリーを再読み込みします。");
      return false;
    }
    return true;
  }

  private async updateEditingDirectories(): Promise<void> {
    const editingDirectories = await this.getFormatTree();
    const directory = this.toHashTableDirectory(editingDirectories);
    const summary = this.toHashTableSummary(editingDirectories);
    this.$refs.treeEditor.overwriteNodes(directory, summary);
    this.selectedDirectory = this.selectedDirectory !== null && directory[this.selectedDirectory.key] instanceof Directory
      ? directory[this.selectedDirectory.key]
      : null;
    this.selectedFormat = this.selectedFormat !== null && summary[this.selectedFormat.id] instanceof EditableFormatSummary
      ? summary[this.selectedFormat.id]
      : null;
  }

  private async getFormatTree(): Promise<Directory<number, EditableFormatSummary>[]> {
    const repository = DomainRegistry.instance.editableFormat.getFormatRepository();
    return repository.getFormatTree();
  }

  private toHashTableDirectory(nodes: Directory<number, EditableFormatSummary>[], array: { [key: number]: Directory<number, EditableFormatSummary> } = {}): { [key: number]: Directory<number, EditableFormatSummary> } {
    Enumerable.from(nodes).forEach(e => {
      array[e.key] = e;
      this.toHashTableDirectory(e.nodes, array);
    });
    return array;
  }

  private toHashTableSummary(nodes: Directory<number, EditableFormatSummary>[], array: { [key: number]: EditableFormatSummary } = {}): { [key: number]: EditableFormatSummary } {
    Enumerable.from(nodes).forEach(n => {
      Enumerable.from(n.entities).forEach(e => {
        array[e.id] = e;
      });
      this.toHashTableSummary(n.nodes, array);
    });
    return array;
  }

  closeEdit(): void {
    this.isVisible = false;
  }
}
